import { useEffect } from 'react';
import { Button, Cell, Container, Divider, Modal, Navigation, Text, Spacer } from '@vinted/web-ui';
import { X24 } from '@vinted/monochrome-icons';
import useTranslate from 'hooks/useTranslate';
import useBreakpoint from 'hooks/useBreakpoint';
import useFetch from 'hooks/useFetch';
import useTracking from 'hooks/useTracking';
import ScrollableArea from 'components/ScrollableArea';
import { ProductItem } from 'components/ProductItem';
import ItemGrid from 'components/ItemGrid';
import ContentLoader from 'components/ContentLoader';
import ErrorState from 'components/ErrorState';
import { getCollection } from 'data/api';
import { transformCollectionResponse } from 'data/api/transformers/response';
import { reloadPage } from 'libs/utils/window';
import { clickEvent, favouriteItemEvent } from 'libs/common/event-tracker/events';
import { ContentSource } from 'constants/tracking/content-sources';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PHONES_MODAL_SIZE = {
  width: '100%',
  height: '100%'
};
const TABLETSUP_MODAL_SIZE = {
  width: 'calc(100% - 32px)',
  height: 'calc(100% - 80px)',
  maxWidth: '1240px'
};

const CollectionViewModal = _ref => {
  let {
    isShown,
    viewingSelf,
    collectionId,
    onClose
  } = _ref;
  const {
    fetch: fetchCollection,
    transformedData: collectionData,
    isLoading,
    error
  } = useFetch(getCollection, transformCollectionResponse);
  const {
    track
  } = useTracking();
  const translate = useTranslate('featured_collection.multiple.view_modal');
  const breakpoints = useBreakpoint();
  const contentDimensions = breakpoints.tabletsUp ? TABLETSUP_MODAL_SIZE : PHONES_MODAL_SIZE;
  useEffect(() => {
    fetchCollection({
      id: collectionId
    });
  }, [fetchCollection, collectionId]);

  function renderNavigation() {
    return /*#__PURE__*/_jsx(Navigation, {
      right: /*#__PURE__*/_jsx(Button, {
        type: Button.Type.Button,
        iconName: X24,
        theme: "amplified",
        styling: Button.Styling.Flat,
        inline: true,
        testId: "collection-view-modal-close-button",
        onClick: onClose
      }),
      body: /*#__PURE__*/_jsx(Text, {
        type: Text.Type.Title,
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center,
        text: translate('title')
      })
    });
  }

  function resolveItemWidth() {
    return 'one-fifth';
  }

  function handleFavouriteToggle(_ref2) {
    let {
      itemId,
      isFollowEvent
    } = _ref2;
    track(clickEvent({
      target: ClickableElement.Favourite
    }));
    track(favouriteItemEvent({
      itemId,
      isFollowEvent,
      contentSource: ContentSource.FeaturedCollection
    }));
  }

  const renderItem = props => {
    const {
      item: {
        data
      }
    } = props;
    return /*#__PURE__*/_jsx(ProductItem, {
      showOwner: false,
      item: data,
      viewingSelf: viewingSelf,
      testId: "collection-item-".concat(data.id),
      showStatus: true,
      onFavouriteToggle: handleFavouriteToggle
    });
  };

  function renderContent() {
    if (error) return /*#__PURE__*/_jsx(ErrorState, {
      onCtaClick: reloadPage
    });

    if (isLoading || !collectionData) {
      return /*#__PURE__*/_jsx(ContentLoader, {
        styling: ContentLoader.Styling.Wide,
        size: ContentLoader.Size.Large,
        testId: "view-modal-loader"
      });
    }

    const gridItems = collectionData.items.map(item => ({
      id: item.id,
      data: item
    }));
    return /*#__PURE__*/_jsxs("div", {
      className: "regular-container",
      children: [/*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsx(Cell, {
        children: /*#__PURE__*/_jsx(Text, {
          type: breakpoints.tabletsUp ? Text.Type.Heading : Text.Type.Title,
          text: collectionData.title
        })
      }), /*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsx(Container, {
        styling: Container.Styling.Narrow,
        children: /*#__PURE__*/_jsx(ItemGrid, {
          items: gridItems,
          renderItem: renderItem,
          widthResolver: resolveItemWidth
        })
      })]
    });
  }

  return /*#__PURE__*/_jsx(Modal, {
    show: isShown,
    hasScrollableContent: true,
    contentDimensions: contentDimensions,
    "data-testid": "collection-view-modal",
    children: /*#__PURE__*/_jsxs("div", {
      className: "u-fill-width",
      children: [renderNavigation(), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsx(ScrollableArea, {
        children: renderContent()
      })]
    })
  });
};

export default CollectionViewModal;